import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, NavLink as RouterNavLink } from 'react-router-dom';
import { useAuth, useFirebase } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { onSnapshot, doc, addDoc, collection, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, updateProfile } from 'firebase/auth';
import { Lightbulb, LogIn, LogOut } from 'lucide-react';

// Import All Reusable Components
import NavLink from './components/NavLink';
import NavDropdown from './components/NavDropdown';
import AuthModal from './components/AuthModal';
import ThemeToggleButton from './components/ThemeToggleButton';
import SuggestionsModal from './components/SuggestionsModal';
import ThreeHeroBanner from './components/ThreeHeroBanner';

// Import All Page Components
import RTOGCalculator from './pages/RTOGCalculator';
import AdvancedMetrics from './pages/AdvancedMetrics';
import InstitutionalData from './pages/InstitutionalData';
import EducationPage from './pages/EducationPage';
import ArticleSummariesPage from './pages/ArticleSummariesPage';
import CpdLogPage from './pages/CpdLogPage';
import CpdAssistantPage from './pages/CpdAssistantPage';
import TrendReportPage from './pages/TrendReportPage';
import AboutPage from './pages/AboutPage';
import BedEqd2Calculator from './pages/BedEqd2Calculator';
import ReviewSuggestions from './pages/ReviewSuggestions';
import UserProfilePage from './pages/UserProfilePage';
import BlogPage from './pages/BlogPage';
import PostPage from './pages/PostPage';
import PricingPage from './pages/PricingPage';

function MainApp() {
    const { user, auth } = useAuth();
    const { subscription } = useSubscription();
    const { db } = useFirebase();
    const navigate = useNavigate();
    
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authError, setAuthError] = useState('');
    const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
    const [useInstitutionalData, setUseInstitutionalData] = useState(false);
    const [institutionalData, setInstitutionalData] = useState(null);
    const [logToEdit, setLogToEdit] = useState(null);
    const [suggestionText, setSuggestionText] = useState('');
    const [isSubmittingSuggestion, setIsSubmittingSuggestion] = useState(false);
    const [suggestionStatus, setSuggestionStatus] = useState(null);

    // --- START OF NEW CODE ---
    // State to hold the function we want to run after a user logs in
    const [postLoginAction, setPostLoginAction] = useState(null);

    // This effect runs whenever the user's status changes
    useEffect(() => {
        // If the user is now logged in AND there's a pending action...
        if (user && !user.isAnonymous && postLoginAction) {
            postLoginAction(); // ...execute that action (e.g., redirect to Stripe)
            setPostLoginAction(null); // Clear the action so it doesn't run again on a page refresh
        }
    }, [user, postLoginAction]);
    // --- END OF NEW CODE ---

    useEffect(() => {
        if (!db || !user?.uid || user.isAnonymous) {
            setInstitutionalData(null); 
            return;
        }
        const unsub = onSnapshot(doc(db, `users/${user.uid}/institutionalData`, 'customValues'), (docSnap) => {
            setInstitutionalData(docSnap.exists() ? docSnap.data() : null);
        });
        return () => unsub();
    }, [db, user]);

    // Handlers for global actions
    const handleLoginRequest = () => { setAuthError(''); setShowAuthModal(true); };
    
    // --- START OF NEW CODE ---
    // A new handler to be passed to child components like the Pricing Page.
    // It saves the action, then opens the login modal.
    const requestLoginAndSetAction = (action) => {
        // We wrap the action in another function to prevent it from executing immediately
        setPostLoginAction(() => () => action()); 
        handleLoginRequest();
    };
    // --- END OF NEW CODE ---

    const handleLogin = (email, password) => signInWithEmailAndPassword(auth, email, password).then(() => setShowAuthModal(false)).catch(e => setAuthError(e.message));
    const handleSignup = (email, password, name) => createUserWithEmailAndPassword(auth, email, password).then(cred => updateProfile(cred.user, {displayName: name})).then(() => setShowAuthModal(false)).catch(e => setAuthError(e.message));
    const handleGoogleSignIn = () => signInWithPopup(auth, new GoogleAuthProvider()).then(() => setShowAuthModal(false)).catch(e => setAuthError(e.message));
    const handleLogout = () => signOut(auth);

    const handleSendSuggestion = async () => {
        if (!suggestionText.trim() || !db) return;
        setIsSubmittingSuggestion(true);
        try {
            await addDoc(collection(db, 'suggestions'), { suggestionText, timestamp: serverTimestamp(), userId: user?.uid || 'anonymous', upvotes: [] });
            setSuggestionStatus('success');
            setSuggestionText('');
        } catch (error) {
            setSuggestionStatus('error');
        }
        setIsSubmittingSuggestion(false);
        setTimeout(() => {
            setShowSuggestionsModal(false);
            setSuggestionStatus(null);
        }, 2000);
    };
    
    const handleLogCpd = async (logData) => {
        if (!db || !user || user.isAnonymous) throw new Error('You must be logged in to save a CPD log.');
        
        let docId = logData.id || logData.articleId;
        if (!docId) { 
            docId = `custom-${new Date().getTime()}`;
            logData.id = docId;
        }
        
        const logRef = doc(db, 'users', user.uid, 'cpdLogs', docId);

        if (logToEdit) {
            return updateDoc(logRef, { ...logData, updatedAt: serverTimestamp() });
        } else {
            return setDoc(logRef, { ...logData, userId: user.uid, completedAt: serverTimestamp() });
        }
    };
    
    const handleEditLog = (log) => { setLogToEdit(log); navigate('/cpd-assistant'); };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
            <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} error={authError} setAuthError={setAuthError} onLogin={handleLogin} onSignup={handleSignup} onGoogleSignIn={handleGoogleSignIn} />
            
            <div className="relative w-screen">
                <ThreeHeroBanner />
                <div className="absolute top-2 right-2 md:top-4 md:right-4 flex items-center space-x-4 bg-black/50 p-2 rounded-lg">
                    <ThemeToggleButton />
                    {user && user.displayName && <span className="text-sm text-white hidden md:block">{user.displayName}</span>}
                    {user && !user.isAnonymous ? (
                        <button onClick={handleLogout} className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg font-semibold shadow-md hover:bg-red-700 transition duration-200">
                            <LogOut size={18} className="mr-2" /> Logout
                        </button>
                    ) : (
                        <button onClick={handleLoginRequest} className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200">
                            <LogIn size={18} className="mr-2" /> Login / Sign Up
                        </button>
                    )}
                </div>
            </div>

            <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden p-4 md:p-8 border border-gray-200 dark:border-gray-700 relative z-10 -mt-12">
                <nav className="no-print flex flex-wrap justify-center items-center gap-2 md:gap-4 mb-8">
                    <NavDropdown label="Calculators">
                        <NavLink label="RTOG Calculator" to="/" />
                        <NavLink label="Plan Metric Analysis" to="/advanced" />
                        <NavLink label="BED/EQD2 Calculator" to="/bed" />
                    </NavDropdown>

                    <NavDropdown label="Education">
                        <NavLink label="SABR Education Modules" to="/education" />
                        <NavLink label="Article Summaries" to="/articles" />
                        <NavLink label="Trend Report" to="/trends" />
                        <NavLink label="Blog" to="/blog" />
                    </NavDropdown>

                    <NavDropdown label="Community">
                        <NavLink label="My CPD Log" to="/cpd" />
                        <NavLink label="My Institutional Data" to="/institutional" />
                        <NavLink label="My Profile" to="/profile" />
                        <NavLink label="Review Suggestions" to="/review" />
                        {subscription && !subscription.isActive && (
                            <NavLink label="Upgrade to Pro" to="/pricing" />
                        )}
                    </NavDropdown>

                    <RouterNavLink
                        to="/about"
                        className={({ isActive }) =>
                            `page-link text-sm md:text-base font-medium px-3 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isActive
                                ? "bg-blue-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            }`
                        }
                    >
                        About
                    </RouterNavLink>
                </nav>
                
                <Routes>
                    <Route path="/" element={<RTOGCalculator useInstitutionalData={useInstitutionalData} setUseInstitutionalData={setUseInstitutionalData} institutionalData={institutionalData} />} />
                    <Route path="/advanced" element={<AdvancedMetrics useInstitutionalData={useInstitutionalData} setUseInstitutionalData={setUseInstitutionalData} institutionalData={institutionalData} />} />
                    <Route path="/bed" element={<BedEqd2Calculator />} />
                    <Route path="/education" element={<EducationPage />} />
                    <Route path="/articles" element={<ArticleSummariesPage onLoginRequest={handleLoginRequest} onLogCpd={handleLogCpd} />} />
                    <Route path="/trends" element={<TrendReportPage />} />
                    <Route path="/profile" element={<UserProfilePage />} />
                    <Route path="/review" element={<ReviewSuggestions />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/post/:slug" element={<PostPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/pricing" element={<PricingPage onLoginRequest={requestLoginAndSetAction} />} />
                    <Route path="/cpd" element={<CpdLogPage onEditLog={handleEditLog} />} />
                    <Route path="/cpd-assistant" element={<CpdAssistantPage initialLog={logToEdit} onLogCpd={handleLogCpd} onClose={() => { setLogToEdit(null); navigate('/cpd'); }} />} />
                    <Route path="/institutional" element={<InstitutionalData onLoginRequest={handleLoginRequest} />} />
                </Routes>
            </div>
            
            <footer className="no-print mt-12 text-center text-sm text-gray-600 dark:text-gray-500 p-4">
                <div className="max-w-3xl mx-auto mb-4 text-xs">
                    <p className="font-bold">Disclaimer:</p>
                    <p>This tool is for educational and research purposes only and is not a substitute for qualified clinical judgment. All treatment plans must be verified and approved by a qualified medical professional.</p>
                </div>
                &copy; {new Date().getFullYear()} <a href="https://sabrcalculator.com" className="hover:underline">SABRCalculator.com</a>. All rights reserved.
                <button onClick={() => setShowSuggestionsModal(true)} className="flex items-center justify-center px-4 py-2 mt-4 mx-auto text-blue-600 dark:text-blue-300 bg-gray-200 dark:bg-gray-800 rounded-lg shadow-md hover:bg-gray-300 dark:hover:bg-gray-700 transition duration-200">
                    <Lightbulb size={18} className="mr-2" /> Suggest an Edit
                </button>
            </footer>

            <SuggestionsModal show={showSuggestionsModal} onClose={() => {setShowSuggestionsModal(false); setSuggestionStatus(null);}} onSend={handleSendSuggestion} suggestionText={suggestionText} onTextChange={(e) => setSuggestionText(e.target.value)} isSubmitting={isSubmittingSuggestion} status={suggestionStatus} />
        </div>
    );
}

function App() {
  return (
    <ThemeProvider>
        <SubscriptionProvider>
            <MainApp />
        </SubscriptionProvider>
    </ThemeProvider>
  );
}

export default App;
